import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  // Press feedback (`scale 0.97`) lives on the global `.press` class via tokens.css.
  'inline-flex items-center justify-center gap-2 rounded-md font-medium select-none whitespace-nowrap focus-ring press disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-subtle text-subtle-foreground hover:bg-subtle/80',
        ghost: 'bg-transparent hover:bg-muted',
        outline: 'border border-border bg-transparent hover:bg-muted',
        destructive: 'bg-danger text-danger-foreground hover:bg-danger/90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, type = 'button', ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} type={type} className={cn(buttonVariants({ variant, size }), className)} {...rest} />;
});
